import * as THREE from "three";
import type { EmblemLook, EmblemSheen } from "./skins/looks";
import { KitClock } from "./skins/kit/clock";
import { CLOTH_FRAG, CLOTH_FRAG_HEAD, CLOTH_NORMAL, CLOTH_VERT, CLOTH_VERT_HEAD } from "./flagClothShader";

/**
 * The fortress pennant as living cloth (cosmetics v2, 2026-10-21: "all skins … ideally have unique animations"). Two
 * layers on the pennant's own `_team` material (flagEmblem.ts dresses it; one program for every tier and emblem):
 *
 * - a ripple: a wave travelling from the pole to the fly, pinned at the hoist and growing toward the free end, with its
 *   normals tilted so the folds catch the sun — on top of the idle clip's swing of the pennant node. Each banner moves
 *   its own way (`CLOTH_WAVES`: the frozen Frostfall stiff and slow, the Greenwake soft in a spring breeze…);
 * - a sheen: the worn banner's own touch, read off the painted cloth itself (gold, ruby, pale and dark texels:
 *   flagClothShader.ts) — embroidered gold thread catching a sweep of light, a frost rime creeping in from the edges,
 *   a flame-licked Sunfire edge, an Ashen edge smouldering…; the stock crest has none.
 *
 * Per vertex the cloth carries `aCloth` (`clothCoords`): how far along it from the hoist, how far up between its
 * bottom (tapered) and top edges, its length, and which way the fly lies. Gates (KitClock, shared with the skin kits):
 * Reduced effects / prefers-reduced-motion hold the ripple still and stop every sweep and twinkle (the static parts —
 * the rime, the char — stay: a look, not motion); the low tiers drop the sheen's detail. Presentation only.
 */

/** Each sheen's code in the shader (0 = none: the stock crest). */
export const SHEEN_CODE: Readonly<Record<EmblemSheen, number>> = {
  smoulder: 1, goldThread: 2, rime: 3, dapple: 4, flameEdge: 5, moonSheen: 6, glitter: 7, forge: 8, sandfall: 9, starfield: 10,
};

/** A banner's ripple: amplitude (fraction of the cloth's length at the fly end), speed (rad/s) and waves along the cloth. */
export interface ClothWave { readonly amp: number; readonly speed: number; readonly waves: number }
export const STOCK_WAVE: ClothWave = { amp: 0.05, speed: 3.4, waves: 1.3 };
export const CLOTH_WAVES: Readonly<Partial<Record<EmblemSheen, ClothWave>>> = {
  rime: { amp: 0.03, speed: 1.9, waves: 1.0 },          // frozen stiff: a slow, shallow heave
  dapple: { amp: 0.06, speed: 2.6, waves: 1.5 },        // a soft spring breeze
  flameEdge: { amp: 0.055, speed: 4.6, waves: 1.6 },    // a hot updraft: quick and lively
  smoulder: { amp: 0.045, speed: 2.8, waves: 1.2 },
  sandfall: { amp: 0.04, speed: 3.0, waves: 1.1 },
};

/** The cloth's uniforms: shared by every pennant material (one worn banner at a time). */
export const CLOTH_UNIFORMS = {
  uClothFx: { value: 0 },
  /** amp, speed, waves (ClothWave). */
  uClothWave: { value: new THREE.Vector3(STOCK_WAVE.amp, STOCK_WAVE.speed, STOCK_WAVE.waves) },
  uClothTime: KitClock.time, uClothMotion: KitClock.motion, uClothDetail: KitClock.detail,
};

/** Where a pennant's cloth lies in its mesh's frame: hoist and fly x, the bottom edge's y at each end, the flat top. */
export interface ClothFrame { readonly hoistX: number; readonly flyX: number; readonly hoistLow: number; readonly flyLow: number; readonly yMax: number }

/** A vertex this close to the cloth's end (fraction of its length) counts as on that edge. */
const EDGE_EPS = 0.02;

/** The frame of a pennant whose cloth vertices are `p` (xyz triples, cloth in local XY): the hoist is the taller end. */
export function clothFrame(p: ArrayLike<number>): ClothFrame {
  const n = Math.floor(p.length / 3);
  let xMin = Infinity, xMax = -Infinity, yMax = -Infinity;
  for (let i = 0; i < n; i++) { xMin = Math.min(xMin, p[i * 3]); xMax = Math.max(xMax, p[i * 3]); yMax = Math.max(yMax, p[i * 3 + 1]); }
  const len = xMax - xMin;
  let lowAtMin = Infinity, lowAtMax = Infinity;
  for (let i = 0; i < n; i++) {
    const x = p[i * 3], y = p[i * 3 + 1];
    if (x - xMin <= EDGE_EPS * len) lowAtMin = Math.min(lowAtMin, y);
    if (xMax - x <= EDGE_EPS * len) lowAtMax = Math.min(lowAtMax, y);
  }
  const hoistAtMax = lowAtMax <= lowAtMin;
  return { hoistX: hoistAtMax ? xMax : xMin, flyX: hoistAtMax ? xMin : xMax, hoistLow: hoistAtMax ? lowAtMax : lowAtMin, flyLow: hoistAtMax ? lowAtMin : lowAtMax, yMax };
}

/**
 * `aCloth` for each vertex of `p`: (t, v, length, dir) — t 0 at the hoist to 1 at the fly, v 0 on the tapered bottom
 * edge to 1 on the top edge (at that t), the cloth's length in local units and the sign of the fly's x from the hoist.
 */
export function clothCoords(p: ArrayLike<number>, f: ClothFrame = clothFrame(p)): Float32Array {
  const n = Math.floor(p.length / 3), out = new Float32Array(n * 4);
  const span = f.flyX - f.hoistX, len = Math.abs(span) || 1, dir = span >= 0 ? 1 : -1;
  for (let i = 0; i < n; i++) {
    const t = Math.min(1, Math.max(0, (p[i * 3] - f.hoistX) / (span || 1)));
    const low = f.hoistLow + (f.flyLow - f.hoistLow) * t;
    out[i * 4] = t;
    out[i * 4 + 1] = Math.min(1, Math.max(0, (p[i * 3 + 1] - low) / Math.max(1e-6, f.yMax - low)));
    out[i * 4 + 2] = len;
    out[i * 4 + 3] = dir;
  }
  return out;
}

/**
 * The ripple's offset along the cloth's normal (local units) at `t` along a cloth of `len`, at time `time` — the
 * shader's `clothWave`, mirrored for the tests. Zero at the hoist (the pole holds it), growing toward the fly.
 */
export function clothOffset(t: number, len: number, time: number, w: ClothWave = STOCK_WAVE): number {
  const k = w.waves * Math.PI * 2, ph = k * t - w.speed * time;
  return w.amp * len * Math.pow(t, 1.3) * (Math.sin(ph) + 0.35 * Math.sin(2.3 * ph + 1.3));
}

export class FlagCloth {
  /** Wears a banner's sheen and ripple on every pennant (null: the stock crest's plain ripple). */
  static wear(look: EmblemLook | null): void {
    const sheen = look?.sheen ?? null;
    CLOTH_UNIFORMS.uClothFx.value = sheen ? SHEEN_CODE[sheen] : 0;
    const w = (sheen && CLOTH_WAVES[sheen]) || STOCK_WAVE;
    CLOTH_UNIFORMS.uClothWave.value.set(w.amp, w.speed, w.waves);
  }

  /** Patches the pennant's material with the ripple and the sheen (its other patches chain after this one). */
  static patch(mat: THREE.MeshStandardMaterial): void {
    const prev = mat.onBeforeCompile;
    mat.onBeforeCompile = (sh, r) => {
      prev?.call(mat, sh, r);
      Object.assign(sh.uniforms, CLOTH_UNIFORMS);
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", `#include <common>\n${CLOTH_VERT_HEAD}`)
        .replace("#include <beginnormal_vertex>", `#include <beginnormal_vertex>\n${CLOTH_NORMAL}`)
        .replace("#include <begin_vertex>", `#include <begin_vertex>\n${CLOTH_VERT}`);
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", `#include <common>\n${CLOTH_FRAG_HEAD}`)
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>\n${CLOTH_FRAG}`);
    };
    const old = mat.customProgramCacheKey?.() ?? "";
    mat.customProgramCacheKey = () => `${old}-cloth`;
    mat.needsUpdate = true;
  }
}
